import React, { useMemo, useState } from "react";
import moment from "moment";
import {
    MAX_SESSIONS_MONTH,
    MIN_SESSIONS_MONTH,
    SessionRow,
    nextSessionRow,
    sessionEnd,
    sessionStart,
    sessionsTotalHours,
    sortSessionRows,
    validateSessionRows,
} from "../../DeclarationCourse/SessionList";
import { fetchExternalOverlap } from "../../DeclarationCourse/sessionConflicts";
import { MAX_COURSES_PER_MONTH } from "../../../Utils/monthlyQuota";
import { DV2 } from "../theme";
import { dayLabel, hours, monthName, plural, studentFirstName } from "../format";
import { vocab } from "../vocabulary";
import { useDeclarationV2 } from "../DeclarationV2Context";
import WizardShell from "../layout/WizardShell";
import PlanningAside from "../layout/PlanningAside";
import StudentMonthPanel, { studentMonthFigures } from "../layout/StudentMonthPanel";
import { Button, Card, FieldLabel, Notice, TextLink, fieldStyle, useDv2Theme } from "../layout/Ui";
import SessionCard from "../components/SessionCard";
import PastCourseDialog from "../components/PastCourseDialog";
import useMonthSessions from "../components/useMonthSessions";
import { allSessionsOver, enforceEarliestStart, firstSelectableDay, majorityMonth, rowIssues } from "../components/planningRules";

/**
 * Étape 2, séances générées (maquettes 1c et 2c) : une carte par séance,
 * modifiable une à une, l'erreur sur la carte fautive, note facultative,
 * avertissement de quota. Validation à « Suivant » avec les règles de
 * l'ancien parcours (validateSessionRows, chevauchements, cours passé).
 */
const SessionsStep = () => {
    const { teachr, state, update, goTo } = useDeclarationV2();
    const { fontFamily } = useDv2Theme();
    const student = state.student!;
    const child = student.child;
    const rows = state.sessions;
    const minDate = firstSelectableDay(student.activationDate);
    const referenceMonth = majorityMonth(rows) ?? moment().format("YYYY-MM");
    const { sessions: existing, failed } = useMonthSessions(teachr?.id, moment(referenceMonth, "YYYY-MM"));
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const [summaryError, setSummaryError] = useState<string | null>(null);
    const [pastOpen, setPastOpen] = useState(false);
    const [checking, setChecking] = useState(false);

    const issues = useMemo(() => rowIssues(rows, { activationDate: student.activationDate, existing, referenceMonth }), [rows, student.activationDate, existing, referenceMonth]);
    const issueByKey = new Map(issues.map((i) => [i.key, i.message]));
    const total = sessionsTotalHours(rows);
    const figures = studentMonthFigures(child, teachr);
    const isCurrentMonth = referenceMonth === moment().format("YYYY-MM");

    const setRows = (next: SessionRow[]) => {
        setSummaryError(null);
        update({ sessions: next });
    };
    const changeRow = (key: string, patch: Partial<SessionRow>) =>
        setRows(enforceEarliestStart(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)), student.activationDate));
    const removeRow = (key: string) => setRows(rows.filter((row) => row.key !== key));
    const addRow = () => {
        if (rows.length >= MAX_SESSIONS_MONTH) return;
        const row = nextSessionRow(rows);
        setRows([...rows, row]);
        setExpandedKey(row.key);
    };
    const goToIssue = (key: string) => {
        setExpandedKey(key);
        document.getElementById(`dv2-session-${key}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const shiftedNotes = (state.shiftedSessions ?? []).filter((note) => rows.some((row) => row.key === note.key));
    const dismissShifted = () => update({ shiftedSessions: [] });

    const proceed = () => {
        setPastOpen(false);
        update({ sessions: sortSessionRows(rows), shiftedSessions: [], step: "pricing" });
        goTo("pricing");
    };

    const next = async () => {
        const validation = validateSessionRows(rows);
        if (issues.length > 0) {
            const first = issues[0];
            const row = rows.find((r) => r.key === first.key);
            const label = row?.date ? dayLabel(row.date).toLowerCase() : `la séance ${rows.findIndex((r) => r.key === first.key) + 1}`;
            setSummaryError(`${issues.length > 1 ? `${issues.length} séances sont à corriger` : "Une séance est à corriger"}. Commencez par ${label}.`);
            goToIssue(first.key);
            return;
        }
        if (!validation.isValid) {
            setSummaryError(validation.message);
            return;
        }
        if (isCurrentMonth && figures.quota?.reached) {
            setSummaryError(`Quota de ${MAX_COURSES_PER_MONTH} ${vocab.courses()} atteint pour ${studentFirstName(child)} en ${monthName(moment())}. Déplacez vos séances sur le mois suivant.`);
            return;
        }
        if (failed && teachr?.id) {
            setChecking(true);
            const overlap = await fetchExternalOverlap(teachr.id, rows).finally(() => setChecking(false));
            if (overlap) {
                setSummaryError(`La séance ${overlap.index} chevauche ${vocab.theCourse()} déjà déclaré avec ${overlap.studentName} (${overlap.existingSlot}).`);
                return;
            }
        }
        if (allSessionsOver(rows)) {
            setPastOpen(true);
            return;
        }
        proceed();
    };

    const starts = rows.map(sessionStart).filter(Boolean) as moment.Moment[];
    const ends = rows.map(sessionEnd).filter(Boolean) as moment.Moment[];
    const canRemove = rows.length > MIN_SESSIONS_MONTH;

    return (
        <>
            <PastCourseDialog
                open={pastOpen}
                description={starts.length ? <>de <b>{plural(rows.length, "séance")}</b> entre le <b>{dayLabel(moment.min(starts)).toLowerCase()}</b> et le <b>{dayLabel(moment.max(ends)).toLowerCase()}</b></> : null}
                onConfirm={proceed}
                onClose={() => setPastOpen(false)}
            />
            <WizardShell
                title={`Vos ${plural(rows.length, "séance")}`}
                subtitle={`Étape 2 sur 4, ${hours(total)} au total`}
                onBack={() => goTo("planning")}
                asideMobile={<StudentMonthPanel child={child} teachr={teachr} compact />}
                aside={
                    <PlanningAside
                        current={2}
                        planningDetail={`${plural(rows.length, "séance")}, ${hours(total)}`}
                    />
                }
                footer={{
                    onPrevious: () => goTo("planning"),
                    onNext: next,
                    nextLoading: checking,
                    extra: summaryError ? (
                        <Notice tone="danger" id="dv2-sessions-error" action={issues[0] ? <TextLink label="Y aller" onClick={() => goToIssue(issues[0].key)} /> : undefined}>{summaryError}</Notice>
                    ) : undefined,
                }}
            >
                {shiftedNotes.length > 0 && (
                    <Notice tone="info" action={<TextLink label="Compris" onClick={dismissShifted} />}>
                        <b>{shiftedNotes.length > 1 ? `${shiftedNotes.length} séances décalées` : "Une séance décalée"}</b> pour ne pas chevaucher {vocab.aCourse()} déjà {vocab.declared()} :{" "}
                        {shiftedNotes.map((note, i) => `${i > 0 ? ", " : ""}${dayLabel(note.date).toLowerCase()} à ${note.to.replace(":", "h")} (après ${note.studentName})`)}. Ajustez-les si besoin.
                    </Notice>
                )}
                <div className="flex flex-wrap gap-2 justify-between items-center">
                    <div style={{ fontSize: 13, color: DV2.muted }}>Touchez une séance ou son crayon pour ajuster sa date, son heure ou sa durée. L'heure de fin se recalcule toute seule.</div>
                    <div className="flex gap-2">
                        <Button label="Choisir les dates" variant="secondary" size="sm" onClick={() => goTo("planning")} />
                        <Button label="+ Ajouter une séance" variant="secondary" size="sm" onClick={addRow} disabled={rows.length >= MAX_SESSIONS_MONTH} />
                    </div>
                </div>

                <div className="flex flex-col gap-3">
                    {rows.map((row, index) => (
                        <SessionCard
                            key={row.key}
                            row={row}
                            index={index}
                            issue={issueByKey.get(row.key)}
                            expanded={expandedKey === row.key}
                            onToggle={() => setExpandedKey(expandedKey === row.key ? null : row.key)}
                            onChange={(patch) => changeRow(row.key, patch)}
                            onRemove={canRemove ? () => removeRow(row.key) : undefined}
                            minDate={minDate}
                        />
                    ))}
                    {rows.length === 0 && (
                        <Notice tone="info">Aucune séance. <TextLink label="Choisir les dates" onClick={() => goTo("planning")} /></Notice>
                    )}
                </div>

                <Card style={{ padding: "16px 20px" }}>
                    <FieldLabel htmlFor="dv2-note">Note pour {vocab.yourClient()}, facultative</FieldLabel>
                    <textarea
                        id="dv2-note"
                        value={state.note}
                        onChange={(e) => update({ note: e.target.value })}
                        rows={2}
                        placeholder="Ex. : révision du chapitre sur les fonctions avant le contrôle"
                        style={{ ...fieldStyle(), fontFamily, fontWeight: 500, fontSize: 15, resize: "vertical" }}
                    />
                </Card>
            </WizardShell>
        </>
    );
};

export default SessionsStep;
